"use client";

import { useEffect, useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { shouldCountOnKey } from "@/lib/tool-kit/study/tally-keys";

const STORAGE_KEY = "citationeasy-tally-counter-v1";

interface Counter {
  id: number;
  name: string;
  count: number;
}

interface StoredState {
  counters: Counter[];
  selectedId: number;
  nextId: number;
}

const DEFAULT_STATE: StoredState = {
  counters: [{ id: 1, name: "Counter 1", count: 0 }],
  selectedId: 1,
  nextId: 2,
};

function isCounter(value: unknown): value is Counter {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === "number" &&
    typeof candidate.name === "string" &&
    typeof candidate.count === "number"
  );
}

function loadState(): StoredState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return DEFAULT_STATE;
    }
    const parsed = JSON.parse(raw) as Partial<StoredState>;
    if (
      !Array.isArray(parsed.counters) ||
      parsed.counters.length === 0 ||
      !parsed.counters.every(isCounter) ||
      typeof parsed.selectedId !== "number" ||
      typeof parsed.nextId !== "number"
    ) {
      return DEFAULT_STATE;
    }
    return {
      counters: parsed.counters,
      selectedId: parsed.selectedId,
      nextId: parsed.nextId,
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TALLY_COUNTER_STRINGS = {
  defaultCounterName: "Counter",
  shortcut:
    "Keyboard shortcut: press {key} to add 1 to the selected (highlighted) counter. The shortcut is ignored while typing in a text field.",
  spaceKey: "Space",
  select: "Select",
  counterNameAriaLabel: "Counter name",
  remove: "Remove",
  removeAriaLabel: "Remove {name}",
  subtractAriaLabel: "Subtract 1 from {name}",
  addAriaLabel: "Add 1 to {name}",
  reset: "Reset",
  resetAriaLabel: "Reset {name}",
  addCounter: "Add Counter",
  counters: "Counters",
  totalAcrossCounters: "Total across counters",
  selectedCounter: "Selected counter",
  none: "—",
} as const;

function fillTemplate(template: string, name: string): string {
  return template.replace("{name}", name);
}

export default function TallyCounterWidget() {
  const strings = useToolUi(TALLY_COUNTER_STRINGS);
  const [state, setState] = useState<StoredState>(DEFAULT_STATE);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setState(loadState());
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable (private mode, quota) — counting still works.
    }
  }, [state, loaded]);

  function adjustCounter(id: number, delta: number) {
    setState((prev) => ({
      ...prev,
      counters: prev.counters.map((counter) => {
        if (counter.id !== id) {
          return counter;
        }
        return { ...counter, count: Math.max(0, counter.count + delta) };
      }),
    }));
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target =
        event.target instanceof HTMLElement
          ? {
              tagName: event.target.tagName,
              isContentEditable: event.target.isContentEditable,
              role: event.target.getAttribute("role"),
            }
          : null;
      if (!shouldCountOnKey(event, target)) {
        return;
      }
      event.preventDefault();
      setState((prev) => ({
        ...prev,
        counters: prev.counters.map((counter) => {
          if (counter.id !== prev.selectedId) {
            return counter;
          }
          return { ...counter, count: counter.count + 1 };
        }),
      }));
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function handleAdd() {
    setState((prev) => ({
      counters: [
        ...prev.counters,
        {
          id: prev.nextId,
          name: `${strings.defaultCounterName} ${prev.nextId}`,
          count: 0,
        },
      ],
      selectedId: prev.nextId,
      nextId: prev.nextId + 1,
    }));
  }

  function handleRemove(id: number) {
    setState((prev) => {
      const counters = prev.counters.filter((counter) => counter.id !== id);
      if (counters.length === 0) {
        return prev;
      }
      const selectedId =
        prev.selectedId === id ? counters[0].id : prev.selectedId;
      return { ...prev, counters, selectedId };
    });
  }

  function handleRename(id: number, name: string) {
    setState((prev) => ({
      ...prev,
      counters: prev.counters.map((counter) => {
        if (counter.id !== id) {
          return counter;
        }
        return { ...counter, name };
      }),
    }));
  }

  function handleResetCounter(id: number) {
    setState((prev) => ({
      ...prev,
      counters: prev.counters.map((counter) => {
        if (counter.id !== id) {
          return counter;
        }
        return { ...counter, count: 0 };
      }),
    }));
  }

  function handleSelect(id: number) {
    setState((prev) => ({ ...prev, selectedId: id }));
  }

  const total = state.counters.reduce((sum, counter) => sum + counter.count, 0);

  return (
    <>
      {/* Shortcut hint */}
      <p className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-muted-foreground text-sm">
        {renderSlots(strings.shortcut, {
          key: (
            <kbd className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-foreground text-xs">
              {strings.spaceKey}
            </kbd>
          ),
        })}
      </p>

      {/* Counters */}
      <div className="grid gap-4 sm:grid-cols-2">
        {state.counters.map((counter) => {
          const selected = counter.id === state.selectedId;
          const cardClass = selected
            ? "border-primary bg-primary/5"
            : "border-border bg-card";
          return (
            <section
              className={`space-y-4 rounded-lg border p-5 transition-colors ${cardClass}`}
              key={counter.id}
            >
              <div className="flex items-center gap-2">
                <label className="flex cursor-pointer items-center gap-1.5 text-muted-foreground text-xs">
                  <input
                    checked={selected}
                    className="accent-primary"
                    name="selected-counter"
                    onChange={() => handleSelect(counter.id)}
                    type="radio"
                  />
                  {strings.select}
                </label>
                <input
                  aria-label={strings.counterNameAriaLabel}
                  className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 font-medium text-foreground text-sm focus:border-border focus:outline-none focus:ring-2 focus:ring-primary"
                  onChange={(e) => handleRename(counter.id, e.target.value)}
                  value={counter.name}
                />
                <button
                  aria-label={fillTemplate(
                    strings.removeAriaLabel,
                    counter.name
                  )}
                  className="rounded border border-border bg-card px-2 py-0.5 text-muted-foreground text-xs transition-colors hover:bg-muted disabled:opacity-40"
                  disabled={state.counters.length === 1}
                  onClick={() => handleRemove(counter.id)}
                  type="button"
                >
                  {strings.remove}
                </button>
              </div>
              <p className="text-center font-semibold text-5xl text-foreground tabular-nums">
                {counter.count}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  aria-label={fillTemplate(
                    strings.subtractAriaLabel,
                    counter.name
                  )}
                  className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                  disabled={counter.count === 0}
                  onClick={() => adjustCounter(counter.id, -1)}
                  type="button"
                >
                  −1
                </button>
                <button
                  aria-label={fillTemplate(strings.addAriaLabel, counter.name)}
                  className="rounded-lg bg-primary px-8 py-3 font-semibold text-lg text-primary-foreground transition-opacity hover:opacity-90"
                  onClick={() => {
                    handleSelect(counter.id);
                    adjustCounter(counter.id, 1);
                  }}
                  type="button"
                >
                  +1
                </button>
                <button
                  aria-label={fillTemplate(
                    strings.resetAriaLabel,
                    counter.name
                  )}
                  className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                  disabled={counter.count === 0}
                  onClick={() => handleResetCounter(counter.id)}
                  type="button"
                >
                  {strings.reset}
                </button>
              </div>
            </section>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
          onClick={handleAdd}
          type="button"
        >
          {strings.addCounter}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label={strings.counters} value={state.counters.length} />
        <StatCard label={strings.totalAcrossCounters} value={total} />
        <StatCard
          label={strings.selectedCounter}
          value={
            state.counters.find((c) => c.id === state.selectedId)?.name ??
            strings.none
          }
        />
      </div>
    </>
  );
}
